import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ElementRef
} from '@angular/core';
import { SohoDataGridToggleRowEvent, SohoMessageService } from 'ids-enterprise-ng';
import { Observable, SubscriptionLike as ISubscription, Subscription, Subject, of } from 'rxjs';
import * as urlTemplate from 'url-template';
import * as Flatted from 'flatted';

import { LMDataGridBaseComponent } from './lm-datagrid-base.component';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMEditorService } from '@list/editors/lm-editor.service';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMListGridPagingService } from '@list/services/lm-list-grid-paging.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMContextService } from '@services/lm-context.service';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMCompoundFieldService, LMErrorUtilService } from '@services/index';
import { AppConstants, LMUniqueIdService } from '@utils/index';
import {
  LMListEventService,
  LMListGridBuilderService,
  LMListSpecUtilService,
  LMListUtilService
} from './services';
import { LMThemeService } from '@banner/lm-theme.service';

@Component({
  selector: 'lm-list-tree',
  templateUrl: 'lm-list-tree.component.html',
  styles: [ `
    :host {
      display:block;
      overflow: auto;
      height: 100%;
    }
  ` ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMListTreeComponent extends LMDataGridBaseComponent implements AfterViewInit, LMListContentInterface, OnInit, OnDestroy {

  private maxLevels: number;
  private showLevels: number;
  private _selectedTreeDataView: LMListDataView;
  private id = 0;
  private subscriptions: ISubscription[] = [];
  private formatColumnHeaderMessageRules = false;
  private searchFormDataView: LMDataView;
  private implementsCancelablePaging = false;
  private cancelPagingNotifier$: Subject<boolean>;
  private expandedRows: Array<string> = [];

  public dataGridId: string;
  public treeData: Array<LMListDataView|LMListTreeMoreLink> = [];
  public flatDataViewArray: Array<LMListDataView|LMListTreeMoreLink> = [];
  public uiLoadingIndicatorTimeout = 0;
  private listEventSubs: Subscription[] = [];
  private initialBuildComplete = false;

  @Input() cubeOrientation: SohoDataGridSelectable;

  @Output() cubeRowsSelected = new EventEmitter<LMDataView | LMDataViewSet>();

  @Output() listLookupClicked = new EventEmitter<LMListLookupClickedEvent>();

  @Output() listFilterLookupClicked = new EventEmitter<LMListFilterLookupClickedEvent>();
  @Output() listFilterClearClicked = new EventEmitter<LMListFilterClearClickedEvent>();

  constructor(
    protected lmConditionUtilService: LMConditionUtilService,
    protected lmContextService: LMContextService,
    protected lmEditorService: LMEditorService,
    protected lmFormatterService: LMFormatterService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmListEventService: LMListEventService,
    protected lmListGridBuilderService: LMListGridBuilderService,
    protected lmLoggerService: LMLoggerService,
    protected lmThemeService: LMThemeService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmListSpecUtilService: LMListSpecUtilService,
    protected lmListUtilService: LMListUtilService,
    protected lmCompoundFieldService: LMCompoundFieldService,
    protected sohoMessageService: SohoMessageService,
    protected lmUniqueIdService: LMUniqueIdService,
    private changeDetectorRef: ChangeDetectorRef,
    private lmListGridPagingService: LMListGridPagingService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmErrorUtilService: LMErrorUtilService,
    private elementRef: ElementRef,
  ) {
    super(
      lmConditionUtilService,
      lmContextService,
      lmEditorService,
      lmFormatterService,
      lmFormatUtilService,
      lmListEventService,
      lmListGridBuilderService,
      lmLoggerService,
      lmThemeService,
      lmUrlUtilService,
      lmListSpecUtilService,
      lmListUtilService,
      lmCompoundFieldService,
      sohoMessageService,
      lmUniqueIdService,
    );
    this.implementsCancelablePaging = this.lmContextService.isUiPagingRequestWaitTimeEnabled;
  }

  ngOnInit() {
    this.dataGridId = this.listSpec?.busListSpec?.id + '_datagrid';
    this.dataGridAutomationId = 'lm-' + this.listSpec?.busListSpec?.automationId + '-datagrid';

    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
    this.clearEmptyMessage();
    this.validateInitialInputs();
    this.build();
  }

  ngAfterViewInit() {
    // ---------------------------------------------------------------------------------
    // Related lists need to have the soho-datagrid built before fromDataViewChanged()
    // can be called. In most cases fromDataViewChanged can be called directly from the
    // '@Input() fromDataView` input setter. But for initial load of a dependent list
    // the datagrid isn't built before the fromDataView input is called. So we call
    // fromDataViewChanged in a timeout here so that change detection will fire.
    // ---------------------------------------------------------------------------------
    setTimeout(() => {
      this.buildTreeList();
    });
  }

  ngOnDestroy() {
    this.flatDataViewArray = [];
    this.subscriptions.forEach(sub => sub.unsubscribe());  // TODO: lets try to get rid of this!
    this.setComponentDestroyed();
    this.cancelOutstandingPagingRequest();
  }

  protected loadManualData(): void {
    if (this.listSpec.dependentList && this.sohoDataGridComponent && !this.componentDestroyed) {
      this.buildTreeList();
    }
  }

  protected fromDataViewChanged() {
    // todo why is this different -- why not do it like grid
    if (this.listSpec.dependentList && this.sohoDataGridComponent && !this.componentDestroyed) {
      if (this.listState?.fromResourceId !== this.fromDataView?.resourceId) {
        this.listState.treeData = undefined;
      }
      this.buildTreeList();
    }
  }

  private handleCancelableRequest(): void {
    if (this.implementsCancelablePaging && this.cancelPagingNotifier$) {
      this.cancelPagingNotifier$.next(true);
      this.cancelPagingNotifier$.complete();
      this.cancelPagingNotifier$ = null;
    }
  }

  private handleCancelableResponse(response: LMListPagingResponse): boolean {
    if (this.implementsCancelablePaging) {
      if (response.status === 'STARTED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'started', cancelablePagingNotifier: response.cancelablePagingNotifier });
        this.cancelPagingNotifier$ = response.cancelablePagingNotifier;
        return true;
      } else if (response.status === 'WAITING') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'waiting' });
        return true;
      } else if (response.status === 'CANCELED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'canceled' });
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        return true;
      } else {
        this.cancelPagingNotifier$ = null;
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'finished' });
        return false;
      }
    } else {
      return false;
    }
  }

  onBeforePaging(pageOp: LMPageOp, event: SohoDataGridFilteredEvent) {
    const beforePagingEvent: LMListGridPagingEvent = { pageOp, sohoDataGridEvent: event };
    this.lmListEventService.listGridBeforePagingEvent(beforePagingEvent);
  }

  // onColumnFiltered(event: SohoDataGridFilteredEvent, continueFiltering = false): void {
  //   if (this.disabled) {
  //     // -------------------------------------------------------------
  //     // this is here until soho grid.js has a disabled state that
  //     // would restrict the filter bar field entry from messaging
  //     // this function.
  //     // -------------------------------------------------------------
  //     return;
  //   }
  //
  //   if (event.op === 'clear' || !event.conditions || event.conditions.length === 0) {
  //     if (!this.listState.filterCriteria || this.listState.filterCriteria.length === 0) {
  //       // --------------------------------------------------------------------------
  //       // getting a 'filtered' event when closing the filter row. On the close
  //       // filter row event I set a flag and if we are asked to close a filter
  //       // we should only clear the filter if a filter has already ben executed
  //       // --------------------------------------------------------------------------
  //       return;
  //     }
  //   }
  //
  //   if (!continueFiltering) {
  //     this.onBeforePaging('filter', event);
  //     return;
  //   }
  //   // --------------------------------------------------------------------------
  //   // setting filterCriteria input here will causes a filterCriteriaChanged()
  //   // message which will cause filterPage() to be called during the
  //   // ngAfterViewChecked lifecycle event.
  //   // --------------------------------------------------------------------------
  //   const pendingFilterCriteria = this.getFilterCriteria(event.conditions);
  //
  //   if (this.lmListUtilService.areTheSameJson(pendingFilterCriteria, this.filterCriteria)) {
  //     return;
  //   }
  //
  //   if (this.validateFilterCriteria(pendingFilterCriteria)) {
  //     this.filterCriteria = pendingFilterCriteria;
  //     this.lmListEventService.updateFilterCriteriaEvent(this.filterCriteria);
  //   }
  // }

  public onColumnFiltered(event, continueFiltering = false) {
    if (event && event.conditions === undefined) {
      return;
    }

    if (event?.trigger === 'render'
    || (!this.lmListUtilService.isAutoFilterEnabled() && event?.trigger === 'selected')) {
      return;
    }

    if (!continueFiltering) {
      this.onBeforePaging('filter', event);
      return;
    }

    const pendingFilterCriteria = this.getFilterCriteria(event.conditions);

    if (!this.validateFilterCriteria(pendingFilterCriteria)) {
      return;
    }

    this.lmListEventService.updateFilterCriteriaEvent(pendingFilterCriteria);
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();

    const compiledUrlTemplate = urlTemplate.parse(this.listUrlInfo.initialDataUrl);
    const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined });
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    const pageSize = (this.gridOptions && this.gridOptions.pagesize) ? this.gridOptions.pagesize : this.listSpec.pageSize;
    const filterCriteria = pendingFilterCriteria;
    const hasSearchForm = this.lmListUtilService.hasSearchForm(this.listSpec, this.lmListSpecUtilService.isDashBoardContent(this.listSpec));

    this.listState.filterCriteria = this.lmListUtilService.mergeStickyModifier(filterCriteria, this.m_columnfilterOperators, hasSearchForm);
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    this.pagingSubscription = this.lmListGridPagingService
      .filterPage(url, filterCriteria, pageSize, postContent)
      .subscribe(
        (listPagingResponse: LMListPagingResponse) => {

          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

          if (this.handleCancelableResponse(listPagingResponse)) {
            return;
          }

          if (listPagingResponse.status === 'COMPLETED') {
            listPagingResponse.resetSelection = true;
            this.postLoad(listPagingResponse);
            this.listState.filterCriteria = this.lmListUtilService.mergeStickyModifier(filterCriteria, this.m_columnfilterOperators, hasSearchForm);
            this.listState.multiSelectedValues = this.multiSelectedValues;
          } else {
            this.lmErrorUtilService.handleError(
              listPagingResponse.message,
              listPagingResponse.details,
              listPagingResponse.status,
              listPagingResponse.message
            );
            this.dataLoaded(listPagingResponse);
          }

          this.changeDetectorRef.markForCheck();
        },
        error => {
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          this.handleError(error.stack, error.message);
        }
      );
  }

  public build() {
    this.listLookupClicked.subscribe(event => this.lmListEventService.listLookupClickedEvent(event));
    this.listFilterLookupClicked.subscribe(event => this.lmListEventService.listFilterLookupClickedEvent(event));
    this.listFilterClearClicked.subscribe(event => this.lmListEventService.listFilterClearClickedEvent(event));

    this.listEventSubs.push(this.lmListEventService.listGridContinuePagingEvent$.subscribe((pagingEvent: LMListGridPagingEvent) => {
      this.onContinuePaging(pagingEvent);
    }));

    this.allowMultiSelections = this.selectRequestData?.allowMultiSelections || false;
    this.allowMaintainingMultiSelections = this.selectRequestData?.maintainSelectedValues || false;
    this.selectedValuesData = this.selectRequestData?.selectedValuesData;

    this.buildGridOptions();
    this.updateGridOptions();
  }

  protected filterCriteriaChanged() {
    if (this.listState === undefined || this.listState.filterCriteria === undefined) {
      return;
    }

    const hasSearchForm = this.lmListUtilService.hasSearchForm(this.listSpec, this.lmListSpecUtilService.isDashBoardContent(this.listSpec));
    if (this.lmContextService.disableTreeFilterFix() && !hasSearchForm) {
      setTimeout(() => {
        this.filterPage();
      });
    } else {
      if (this.initialBuildComplete) {
        this.filterPage();
      }
    }
  }

  private filterPage(): void {
    this.setEmptyMessage();

    if (this.listSpec.dependentList && !this._fromDataView) {
      return;
    }

    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    const compiledUrlTemplate = urlTemplate.parse(this.listUrlInfo.initialDataUrl);
    const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined });
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);
    const pageSize = (this.gridOptions && this.gridOptions.pagesize) ? this.gridOptions.pagesize : this.listSpec.pageSize;

    this.lmLoggerService.time({ id: 'LMListView.filterPage() ' + this.instanceId });
    this.pagingSubscription = this.lmListGridPagingService.filterPage(url, this.filterCriteria, pageSize, postContent).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        this.lmLoggerService.time({ id: 'LMListView.filterPage() ' + this.instanceId });

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListView.filterPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.listState.filterCriteria = this.filterCriteria;
          this.listState.multiSelectedValues = this.multiSelectedValues;
          // TODO: this code can be removed when the select all checkbox is not shown in selection mode
          if (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) {
            this.doNotClearSelectionTags = true;
          }
          this.postLoad(listPagingResponse);
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }

        this.changeDetectorRef.markForCheck();
      },
      error => {
        this.lmLoggerService.timeEnd('LMListView.filterPage() ' + this.instanceId, [ this ]);
        this.handleError(error.stack, error.message);
      });
  }

  protected filterListCriteriaChanged() {
    return;
  }

  public updateGridOptions() {
    // Set flag if columns have message rules, process after dataViewSet is loaded
    this.formatColumnHeaderMessageRules = this.columnHeadersHaveMessageRules();
    this.gridOptions.treeGrid = true;
    this.gridOptions.paging = false;
    this.gridOptions.indeterminate = false;
    this.gridOptions.pagesize = this.listSpec.pageSize ? this.listSpec.pageSize : 100;
    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      this.gridOptions.selectable = this.cubeOrientation;
    }

    this.gridOptions.selectChildren = (this.gridOptions.selectable === 'siblings') ? true : false;
  }

  public updateChartOrientationForCube() {
    this.gridOptions.selectable = (this.listSpec.listType === 'select')
      ? 'single'
      : (this.lmListSpecUtilService.isCubeView(this.listSpec))
        ? this.cubeOrientation
        : 'mixed';

    this.gridOptions.selectChildren = (this.gridOptions.selectable === 'siblings') ? true : false;
  }

  onContinuePaging(continuePagingEvent: LMListGridPagingEvent) {
    if (continuePagingEvent.pageOp === 'filter') {
      this.onColumnFiltered(continuePagingEvent.sohoDataGridEvent, true);
    }
  }

  public onContextMenu(event: SohoDataGridRowClicked): void {
    // clicked in open area of datagrid. could bring up only class level actions (create or set actions).
    if (!event) {
      return;
    }

    this.sohoDataGridComponent.unSelectAllRows();

    if (!event.originalEvent.clientX || !event.originalEvent.clientY) {
      const domRect: DOMRect = (event.originalEvent.target as HTMLElement).getBoundingClientRect();
      event.originalEvent.clientX = domRect.x;
      event.originalEvent.clientY = domRect.y;
    }

    this.lmListEventService.contextMenuEvent(<LMContextMenuEvent>{
      ...event,
      type: 'grid'
    });
  }

  public getDataViewSet(): LMDataViewSet {
    this.buildFlatDataViewArray();

    // Safety check, don't mutate if object has been frozen
    if (!Object.isFrozen(this.dataViewSet)) {
      this.dataViewSet.data = this.flatDataViewArray as Array<LMDataView>;
    }
    return this.dataViewSet;
  }

  private _getDataView(idx: number) {
    this.buildFlatDataViewArray();
    return this.flatDataViewArray[idx];
  }

  public onRowActivated(event: SohoDataGridRowActivated) {
    // Need to add this for dependent lists
    // buildInitialPageUrl in listGrid expects fromDataView
    // fromDataView is nested inside of node object coming back from tree
    this.listState.activatedRow = event.row;
    event.item = event.item.node;
    this.lmListEventService.listRowActivatedEvent((event));
  }

  /**
   * When the checkbox is selected
   * @param selectedRows
   */
  public onRowsSelected(selectedRows: SohoDataGridSelectedEvent) {
    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      if ( selectedRows.rows && selectedRows.rows.length > 0 ) {
        const row: number = selectedRows.rows[0].idx;
        const item: LMListDataView|LMListTreeMoreLink = this._getDataView(row);

        if (this.cubeOrientation === 'siblings') {
          if (this.checkTreeMoreLinkSelected(selectedRows)) {
            // Clear the chart. Complete data is not available to chart (for eg. pie will not be correct)
            const cloneDVS: LMDataViewSet = JSON.parse(JSON.stringify(this.dataViewSet));
            cloneDVS.data = [];
            this.cubeRowsSelected.emit(cloneDVS);
            return;
          }
          const parent = this.findParentNode(<LMListDataView>item, this.getLMListDataViews(this.treeData));
          let childrenDVSet;
          if (parent) {
            childrenDVSet = parent.dataViewSet;
          } else {
            const cloneDVS: LMDataViewSet = JSON.parse(JSON.stringify(this.dataViewSet));
            cloneDVS.data = [];
            this.treeData.map((mapRow, index) => {
              cloneDVS.data[ index ] = JSON.parse(JSON.stringify(mapRow));
            });
            childrenDVSet = cloneDVS;
          }
          this.cubeRowsSelected.emit(childrenDVSet);
        } else {
          // Single row selection. Chart various categories in single row.
          // lm-list-chart fails if DataViewSet.data is empty. This is bound to happen if the row is not expanded.
          // Period chart uses dataviewset.chartperiods. Use the current row to clone a dvs
          // Column chart uses dataviewset.data[0] to chart elements. Use the current row to clone a dvs. And set data[0]
          const cloneDVS: LMDataViewSet = JSON.parse(JSON.stringify(item));
          cloneDVS.data = [];
          cloneDVS.data[0] = JSON.parse(JSON.stringify(item));
          this.cubeRowsSelected.emit(cloneDVS);
        }
        this.updateDimensionColumnHeader(<LMDataView> item);
      } else if (this.dataViewSet.data.length === 0) {
        // Cube viewer should be notified to clear the chart
        this.cubeRowsSelected.emit(this.dataViewSet);
      }
    }

    if (selectedRows.type === 'deselect' && selectedRows.rows?.length === 0) {
      this.listState.selectedRows = [];
    }

    let listRowSelectedEvent = null;
    if (selectedRows.rows && selectedRows.rows.length === 1) {
      this.setSelectedTreeDataView(<LMListDataView>selectedRows.rows[0].data);

      listRowSelectedEvent = <LMListRowSelectedEvent>{
        event: selectedRows.e,
        rows: <LMListRowData[]>selectedRows.rows,
        type: selectedRows.type,
        rowData: selectedRows.rowData
      };
    } else {
      // When handling a lookup for a multi-select key field emit all selected rows
      if (this.allowMultiSelections) {
        listRowSelectedEvent = <LMListRowSelectedEvent>{
          event: selectedRows.e,
          rows: <LMListRowData[]>selectedRows.rows,
          type: selectedRows.type,
          rowData: selectedRows.rowData,
          doNotClearSelectionTags: this.doNotClearSelectionTags
        };
      }
    }

    this.doNotClearSelectionTags = false;
    this.lmListEventService.listRowSelectedEvent(listRowSelectedEvent);
  }

  private checkTreeMoreLinkSelected(selectedRows: SohoDataGridSelectedEvent): boolean {
    if ( selectedRows.rows && selectedRows.rows.length > 0 ) {
      let isMoreLink = false;
      selectedRows.rows.forEach((row: SohoDataGridSelectedRow) => {
        if (row.data && row.data.isTreeMoreLink) {
          isMoreLink = true;
        }
      });
      return isMoreLink;
    }
    return false;
  }

  public onRowDoubleClicked(event: SohoDataGridRowClicked): void {
    // Safety if double click on filter row and no event is returned
    if (!event) {
      return;
    }

    const row: number = event.row;
    const item = this._getDataView(row) as LMDataView;
    this.setSelectedTreeDataView(item);

    this.lmListEventService.listRowDoubleClickedEvent(<LMListRowClickedEvent>event);
  }

  // commented out due to datagrid-base already doing this and datagrid-base has a
  // more complete implementation. - Phillip 2/5/2020
  // /**
  //  * When the row is clicked anywhere
  //  * @param event
  //  */
  // public onRowClicked(event: SohoDataGridRowClicked) {
  //   if (!event) {
  //     return; // safety - event can be null if user clicks on the datagrid background.
  //   }
  //
  //   const row: number = event.row;
  //   const item = this._getDataView(row) as LMDataView;
  //   this.setSelectedTreeDataView(item);
  //
  //   // ---------------------------------------------------------------------
  //   // if no selection mode is set
  //   // allow a single click to open a record
  //   // ---------------------------------------------------------------------
  //   if (!this.gridOptions.selectable) {
  //     this.lmListEventService.listRowClickedEvent(<LMListRowClickedEvent>{
  //       cell: event.cell,
  //       item: event.item,
  //       originalEvent: event.originalEvent,
  //       row: event.row
  //     });
  //   }
  // }

  private saveExpandedRows(data: Array<any>) {
    data.forEach((item) => {
      if (item.children && item.children.length > 0) {
        this.saveExpandedRows(item.children);
      }

      if (item.expanded) {
        this.expandedRows.push(item.cubeInfo ? item.dataViewResId : item.resourceId);
      }
    });

    this.listState.expandedRows = this.expandedRows;
  }

  public refresh(): Observable<boolean> {
    if (this.isBusyPaging()) {
      return of (true);
    }
    // console.log('listtree-refresh: ' + this.listSpec.busListSpec.name);
    this.expandedRows = [];
    this.saveExpandedRows(this.treeData);
    this.clearDirty();
    // TODO: this code can be removed when the select all checkbox is not shown in selection mode
    if (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) {
      this.doNotClearSelectionTags = true;
    }
    this.sohoDataGridComponent.unSelectAllRows();
    this.listState.activatedRow = (this.listState.activatedRow) ? this.listState.activatedRow : 0;
    return this.refreshTreeList();
  }

  public onCollapseRow(e: SohoDataGridToggleRowEvent) {
    const isCubeView = this.lmListSpecUtilService.isCubeView(this.listSpec);
    const rowResourceId: Array<any> = isCubeView ? [ e.rowData.dataViewResId ] : [ e.rowData.resourceId ];
    const rowDimensionField = isCubeView ? e.rowData.fields['Dimension_cube_view_dimension'].value : undefined;
    const rowId = e.rowData.id;

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    this.treeData.map(function callback(dataView: LMListDataView ) {
      const resourceId = isCubeView ? dataView.dataViewResId : dataView.resourceId;
      const dimensionField = isCubeView && !dataView.isTreeMoreLink ? dataView.fields['Dimension_cube_view_dimension'].value : undefined;
      if (rowResourceId.includes(resourceId)
          && rowDimensionField === dimensionField
          && dataView.depth >= e.rowData.depth
          && rowId === dataView.id) {
        dataView.expanded = false;
        // Setting dataView children to empty set of nodes causes the tree to re-render and all the indices are off. It looks totally broken
        // dataView.children = [];
      }

      if (Array.isArray(dataView.children)) {
        dataView.children.map(callback);
      }
    });

    this.changeDetectorRef.detectChanges();
    // Activating should call the Soho's activetRow so that soho has a chance to manage
    // the activation. listState and onRowActivated will automatically be called as a
    // result of calling activateRow. - Phillip 2/5/2020
    this.performSelectionForMode(e.row);
  }

  public onExpandRow(e: SohoDataGridToggleRowEvent) {

    // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
    // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
    // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
    // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

    let childrenUrl = e.rowData.childrenUrl;

    // let postContent: string;
    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      const delimiter = (childrenUrl.indexOf('?') === -1) ? '?' : '&';
      childrenUrl = childrenUrl + delimiter + this.lmUrlUtilService.objectToCubeQueryString(e.rowData.cubeInfo);
      // postContent = JSON.stringify(e.rowData.cubeInfo);
      childrenUrl = this.addCubeOverrideInfo(childrenUrl);
    }

    // todo khollenbeck ppatton how to deal with a childrenUrl being undefined with new listSpec.childrenUrl template?
    if (!e.rowData.childrenLoaded && e.rowData.childrenUrl !== undefined) {

      this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
      this.handleCancelableRequest();
      this.cancelOutstandingPagingRequest();

      this.pagingSubscription = this.lmListGridPagingService
        .initialPageFiltered(childrenUrl, null, this.gridOptions.pagesize/*, postContent*/)
        .subscribe((listPagingResponse: LMListPagingResponse) => {

          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

          if (this.handleCancelableResponse(listPagingResponse)) {
            return;
          }
          if (listPagingResponse.status !== 'COMPLETED') {
            this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details, listPagingResponse.status, listPagingResponse.statusText);
            return;
          }

          const children = this.getChildren(listPagingResponse.dataViewSet, e.rowData);
          e.rowData.children = children;

          if (children.length === 0) {
            delete e.rowData.children;
          }

          const isCubeView = this.lmListSpecUtilService.isCubeView(this.listSpec);
          let parentResourceId = e.rowData.resourceId;
          const parentRowId = e.rowData.id;
          let parentDimensionField;
          if (isCubeView) {
            e.rowData.dataViewSet = listPagingResponse.dataViewSet;
            parentResourceId = e.rowData.dataViewResId;
            parentDimensionField = e.rowData.fields['Dimension_cube_view_dimension'].value;
          }
          const treeListComponent = this;
          let parentId = 0;
          // Need to reset the ID, the IDS will need to be updated as new children are added
          this.resetID();

          // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
          this.treeData.map(function callback(dataView: LMListDataView ) {
            dataView.id = treeListComponent.getID();
            dataView.parentId = parentId;

            if (!dataView.isTreeMoreLink) {
              const dataViewResId = isCubeView ? dataView.dataViewResId : dataView.resourceId;
              const dimensionField = isCubeView ? dataView.fields['Dimension_cube_view_dimension'].value : undefined;
              const currentRowId = dataView.id;
              if (dataViewResId === parentResourceId && parentDimensionField === dimensionField && parentRowId === currentRowId) {
                dataView.expanded = true;
              }
            }

            if (Array.isArray(dataView.children)) {
              parentId = dataView.id;
              dataView.children.map(callback);
              parentId = dataView.parentId;
            }
          });

          this.update(listPagingResponse, false, e.row);
          this.changeDetectorRef.markForCheck();

          // Call is performed in a timeout to make sure the changes to the
          // tree have occurred before attempting selection model for the mode.
          setTimeout(() => {
            this.performSelectionForMode(e.row);
            this.lmListEventService.listGridAfterPagingEvent(listPagingResponse);
          });
        });
    }
  }

  private performSelectionForMode(row: number) {
    // if single select use select vs activate row
    const singleSelect = this.lmListSpecUtilService.getSelectionMode(this.listSpec, this.inSelectListEditMode) === 'single';
    if (singleSelect) {
      if (row !== undefined) {
        this.setSelectedRows([ row ]);
      }
    } else {
      this.activateRow(row);
    }
  }

  public showSelectAllCheckBox(showCheckBoxInHeader: boolean): void {
    // TODO: uncomment when the select all checkbox in selection mode is removed
    // const selectAllCheckbox = this.elementRef.nativeElement.closest('.modal-content').querySelector('thead th[data-column-id="selectionCheckbox"]');
    // const checkbox = selectAllCheckbox?.querySelector('span.datagrid-checkbox');
    // if (checkbox) {
    //   checkbox.style.display = (showCheckBoxInHeader) ? '' : 'none';
    // }
  }

  public onColumnClicked(columnDef: any, event: any, args: any) {
    const isTreeMoreLink = event.target.classList.contains('lm-more-link');
    const row = args[ 0 ].row;
    const cell = args[ 0 ].cell;
    const dataView = args[ 0 ].item;

    if (isTreeMoreLink) {
      const parentDataView = <LMDataView> this.flatDataViewArray.find((dv) => dv.id === dataView.parentId);
      this.loadMoreRecords(dataView.pagingUrls, parentDataView);
    } else if (columnDef.displayField.cubeDrill ||
        columnDef.displayField.linkNavigation ||
        columnDef.displayField.navigation ||
        columnDef.displayField.httpUrl) {
      // if (this.gridOptions.selectable !== 'mixed')
      if (columnDef.displayField.cubeDrill) {
        // Cube selection is not mixed and row activated event is not triggered on click.datagrid
        // Save of the row activated in list state so the right row is activated on return from cube drill
        this.listState.activatedRow = row;
        this.listState.selectedRows = this.getSelectedRows();
        this.listState.selectedRows = (this.listState.selectedRows.length === 0) ?
          [ this.listState.activatedRow ] : this.listState.selectedRows;
      }
      this.lmListEventService.uiLinkEvent({ columnDef, dataView, event });
    } else if (columnDef.displayField.type === AppConstants.FORMBUTTON) {
      this.lmListEventService.formButtonEvent({ columnDef, dataView, row, cell, event });
    }
  }

  public getSelectedTreeDataView(): LMListDataView {
    return this._selectedTreeDataView;
  }

  public cubeReload(updateColumnHeaders = false) {
    const reloadUrl = (this.dataViewSet) ? this.addCubeOverrideInfo(this.dataViewSet.pagingUrls.reloadPageUrl) : this.getInitialUrl();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.handleCancelableRequest();
    const subscriber = this.lmListGridPagingService.reloadPage2(reloadUrl)
      .subscribe((listPagingResponse: LMListPagingResponse) => {

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        if (listPagingResponse.status === 'COMPLETED') {
          this.treeData = [];
          // TODO: use list state service methods to update
          this.listState.selectedRows = this.findAllLevel1RowIndices();
          this.postLoad(listPagingResponse);
          if (listPagingResponse.dataViewSet.data.length === 0) {
            // When data is available, the rowSelected event will update dimension column header.
            this.updateDimensionColumnHeader(listPagingResponse.dataViewSet.header);
          }
          if (updateColumnHeaders) {
            // Update the column headers, even if the dateset is empty.
            const overridePeriod = this.listState.cubeOverrides && this.listState.cubeOverrides.periodOverrides && this.listState.cubeOverrides.periodOverrides.hasOwnProperty('overridePeriods');
            this.updateColumnsAfterPeriodOverride(listPagingResponse.dataViewSet, !overridePeriod);
          }

          this.changeDetectorRef.markForCheck();
        } else {
          this.lmErrorUtilService.handleError(listPagingResponse.message, listPagingResponse.details,
            listPagingResponse.status, listPagingResponse.message);
        }
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.lmErrorUtilService.handleError(error);
      });

    this.subscriptions.push(subscriber);
  }

  private getPageSize(): number {
    return (this.gridOptions && this.gridOptions.pagesize) ? this.gridOptions.pagesize : this.listSpec.pageSize;
  }

  private getUrlTemplate() {
    return urlTemplate.parse(this.listUrlInfo.initialDataUrl);
  }

  private getInitialUrl(): string {
    let url = this.getUrlTemplate().expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined });

    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      if (this.lmListSpecUtilService.isDashBoardContent(this.listSpec)) {
        let delimiter = (url.indexOf('?') > -1) ? '&' : '?';
        // Add dimension for Dashboard panel cube views.
        if (this.listSpec.cubeOptions.dimensionName) {
          url = url + delimiter + 'dimensionName=' + this.listSpec.cubeOptions.dimensionName;
          delimiter = '&';
        }
        url = url + delimiter + 'dashboardView=true';
      } else {
        url = this.addCubeOverrideInfo(url);
      }

    }

    return url;
  }

  private refreshTreeList(): Observable<boolean> {
    return new Observable<any>(observer => {
      this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
      this.handleCancelableRequest();
      this.cancelOutstandingPagingRequest();

      if (this.listSpec.dependentList) {
        if (this.fromDataView === undefined) {
          observer.next(true);
          observer.complete();
          return; // safety
        }

        if (this.fromDataView === null) {
          // disabled flag should be set at this point! That makes disabling
          // the component here seem like we are doing disabled checking in 2 spots.
          // todo: ppatton Look into more consistent (and single) way of disabling list-grid, list-view, etc...
          this.disableComponent();

          /**
           * emitting the dataLoaded event with a null so the lm-list object
           * can respond appropriately.
           * todo: ppatton again, why do this here?! shouldn't the lm-list already know it's been disabled?!
           */
          setTimeout(() => this.lmListEventService.dataLoadedEvent(null));
          observer.next(true);
          observer.complete();
          return;
        }
      }
      const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);

      this.pagingSubscription = this.lmListGridPagingService
        .initialPageFiltered(this.getInitialUrl(), this.filterCriteria, this.getPageSize(), postContent, this.isManualLoading())
        .subscribe(async (listPagingResponse: LMListPagingResponse) => {

          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

          if (this.handleCancelableResponse(listPagingResponse)) {
            observer.next(true);
            observer.complete();
            return;
          }

          this.setEmptyMessage();

          if (listPagingResponse.status === 'COMPLETED') {
            listPagingResponse = await this.loadChildren(listPagingResponse);
            // this.loadChildren(listPagingResponse).then((r) => listPagingResponse = r);
            this.listState.multiSelectedValues = this.multiSelectedValues;
            // TODO: this code can be removed when the select all checkbox is not shown in selection mode
            if (this.allowMaintainingMultiSelections && !this.inSelectListEditMode) {
              this.doNotClearSelectionTags = true;
            }
            this.postLoad(listPagingResponse);

            if (listPagingResponse.searchDataView) {
              this.searchFormDataView = listPagingResponse.searchDataView;
              this.listState.searchFormDataView = this.searchFormDataView;
            }

            if (this.listState.cubeOverrides?.periodOverrides?.periodOverride || this.listSpec.busListSpec.hasChildHierarchyDims) {
              this.updateColumnsAfterPeriodOverride(listPagingResponse.dataViewSet);
            }
          } else {
            this.handleError(listPagingResponse.details, listPagingResponse.statusText);
            this.dataLoaded(listPagingResponse);
          }

          observer.next(listPagingResponse.status === 'COMPLETED');
          observer.complete();
          this.changeDetectorRef.markForCheck();
        },
        error => {
          observer.next(false);
          observer.complete();
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          this.handleError(error.stack, error.message);
        });
    });
  }

  private async loadChildren(listPagingResponse: LMListPagingResponse): Promise<LMListPagingResponse> {
    const self = this;
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    await Promise.all(listPagingResponse.dataViewSet.data.map(async function callback(dataView: LMListDataView) {
      if (!self.expandedRows.includes(dataView.cubeInfo ? dataView.dataViewResId : dataView.resourceId) &&
        (!dataView.isSubtotalView && !dataView.isSubtotalSummaryView)
      ) {
        dataView.children = [];
      } else if (!dataView.isSubtotalView && !dataView.isSubtotalSummaryView) {
        let childrenUrl = dataView.childrenUrl;
        if (self.lmListSpecUtilService.isCubeView(self.listSpec)) {
          const delimiter = (childrenUrl.indexOf('?') === -1) ? '?' : '&';
          childrenUrl = childrenUrl + delimiter + self.lmUrlUtilService.objectToCubeQueryString(dataView.cubeInfo);
          childrenUrl = self.addCubeOverrideInfo(childrenUrl);
        }

        if (dataView.isLeaf) {
          return;
        }

        dataView.children = await self.getChildData(childrenUrl);
        dataView.expanded = true;
        await Promise.all(dataView.children.map(callback));
      }
    }));

    return listPagingResponse;
  }

  private async getChildData(url: string): Promise<Array<LMDataView>> {
    return new Promise((resolve, reject) => {
      this.handleCancelableRequest();
      this.lmListGridPagingService
        .initialPageFiltered(url, undefined, this.getPageSize(), undefined)
        .subscribe((listPagingResponse: LMListPagingResponse) => {
          if (this.handleCancelableResponse(listPagingResponse)) {
            if (listPagingResponse.status === 'CANCELED') {
              resolve(undefined);
            }
            // Continue to wait for the complete timing event
            return;
          }

          resolve(listPagingResponse.dataViewSet.data);
        },
        error => {
          reject(error);
        });
    });
  }

  private buildTreeList() {
    if (this.listSpec.dependentList) {
      if (this.fromDataView === undefined) {
        return; // safety
      }

      if (this.fromDataView === null) {
        // disabled flag should be set at this point! That makes disabling
        // the component here seem like we are doing disabled checking in 2 spots.
        // todo: ppatton Look into more consistent (and single) way of disabling list-grid, list-view, etc...
        this.disableComponent();

        /**
         * emitting the dataLoaded event with a null so the lm-list object
         * can responsd appropriately.
         * todo: ppatton again, why do this here?! shouldn't the lm-list already now it's been disabled?!
         */
        setTimeout(() => this.lmListEventService.dataLoadedEvent(null));
        return;
      }

      if (this.isManualLoading()) {
        this.listState.treeData = undefined;
      }
    }

    const loadFromPageState = this.allowsLoadingFromPageState() ? this.listState.treeData !== undefined : false;
    if (loadFromPageState) {
      this.loadFromState();
      this.initialBuildComplete = true;
      return;
    }

    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();

    const url = this.getInitialUrl();

    // todo: fix in next sprint - current filter criteria should be applied
    //  when fromDataviewChanges
    if (this.listSpec.dependentList) {
      this.m_filterCriteria = [];
      delete this.listState?.filterCriteria;
    }
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);

    this.pagingSubscription = this.lmListGridPagingService
      .initialPageFiltered(url, this.filterCriteria, this.getPageSize(), postContent, this.isManualLoading())
      .subscribe((listPagingResponse: LMListPagingResponse) => {

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        this.setEmptyMessage();

        if (listPagingResponse.status === 'COMPLETED') {
          // TODO: CAL - temporary workaround for https://jira.lawson.com/browse/LMRK-99282
          if (this.isManualLoading()) {
            listPagingResponse.dataViewSet.data = [];
          }

          this.postLoad(listPagingResponse);

          if (listPagingResponse.searchDataView) {
            this.searchFormDataView = listPagingResponse.searchDataView;
            this.listState.searchFormDataView = this.searchFormDataView;
          }

          if (this.listState.cubeOverrides?.periodOverrides?.periodOverride || this.listSpec.busListSpec.hasChildHierarchyDims) {
            this.updateColumnsAfterPeriodOverride(listPagingResponse.dataViewSet);
          }
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
          this.dataLoaded(listPagingResponse);
        }

        this.initialBuildComplete = true;
        this.changeDetectorRef.markForCheck();
      },
      error => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      });
  }

  private formatDataViewsForTree(listPagingResponse: LMListPagingResponse) {
    const dataViewSet = listPagingResponse.dataViewSet;
    const data = dataViewSet.data;
    let depth = 1;
    const parentId = 0;
    this.treeData = [];

    if (data.length === 0) {
      return;
    }

    data.forEach((dataView: LMDataView, index) => {

      // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
      // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
      // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
      // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

      // todo khollenbeck ppatton how to deal with a childrenUrl being undefined with new listSpec.childrenUrl template?


      const treeDataView: LMListDataView = Object.assign({
        depth,
        parentId,
        children: this.hasChildren(dataView),
        pagingInfo: dataViewSet.pagingInfo
      }, dataView);

      if (treeDataView.children === undefined) {
        delete treeDataView.children;
      }

      Object.keys(treeDataView.fields)
        .filter(key => treeDataView.fields.hasOwnProperty(key))
        .forEach(key => treeDataView[key] = treeDataView.fields[key].value);

      // for (const key in treeDataView.fields) {
      //   if (!treeDataView.fields.hasOwnProperty(key)) {
      //     continue;
      //   }
      //   treeDataView[key] = treeDataView.fields[key].value;
      // }

      // SET ID
      treeDataView.id = this.getID();

      if (treeDataView.dataViewSet && depth <= this.showLevels) {
        depth++;
        // treeDataView.expanded = true;
        treeDataView.children = this.getChildren(treeDataView.dataViewSet, treeDataView);
      }

      this.treeData.push(treeDataView);

      if (index + 1 === data.length && (dataViewSet.pagingInfo && dataViewSet.pagingInfo.hasNext)) {
        this.treeData.push(<LMListTreeMoreLink>{
          depth: 1,
          isTreeMoreLink: true,
          pagingUrls: dataViewSet.pagingUrls,
          id: this.getID(),
          cubeInfo: this.lmListSpecUtilService.isCubeView(this.listSpec) ? { cubeMediator: 'cubeList' } : undefined
        });
      }

      // reset depth
      depth = 1;
    });

    if (this.listState.activatedRow && this.listState.activatedRow > -1) {
      const flatDataViewSet = this.getDataViewSet();
      if (this.listState.activatedRow > flatDataViewSet.data.length - 1) {
        this.listState.activatedRow = 0;
      }
    } else {
      this.listState.activatedRow = 0;
    }
  }

  private resetID(): void {
    this.id = 0;
  }

  private getID(): number {
    this.id = this.id + 1;
    return this.id;
  }

  public setSelectListFocus() {
    if (!this.lmContextService.useSingleSelectList() || !this.lmListSpecUtilService.isSelectList(this.listSpec)) {
      return;
    }

    const selectedRow = this.getSelectedRow();
    if (selectedRow === -1) {
      return;
    }

    this.sohoDataGridComponent.setActiveCell(selectedRow, 0);
  }

  private loadMoreRecords(pagingUrls: LMListPagingUrls, parentDataView: LMDataView) {
    // console.log('listtree-loadMoreRecords: ' + this.listSpec.busListSpec.name);
    let url = pagingUrls.nextPageUrl;
    const isCubeView = this.lmListSpecUtilService.isCubeView(this.listSpec);
    if ( isCubeView ) {
      // There are tree displays in dashboard view, so there may be paging.
      // But we do not want the dashboard flag for paging
      if (!this.lmListSpecUtilService.isDashBoardContent(this.listSpec)) {
        url = this.addCubeOverrideInfo(url);
      }
    }

    if (parentDataView) {
      // Add the dataview cube info to the url. This will give the current dimension where we are paging.
      const delimiter = (url.indexOf('?') === -1) ? '?' : '&';

      if (isCubeView) {
        url = url + delimiter + 'parent=' + parentDataView.dataViewResId + '&' + this.lmUrlUtilService.objectToCubeQueryString(parentDataView.cubeInfo);
      } else {
        url = url + delimiter + 'parent=' + parentDataView.resourceId;
      }
    }

    const pageSize = (this.gridOptions && this.gridOptions.pagesize) ? this.gridOptions.pagesize : this.listSpec.pageSize;
    const postContent = this.lmListGridPagingService.buildPostContent(this.listUrlInfo, this.selectRequestData);


    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();

    this.pagingSubscription = this.lmListGridPagingService
      .initialPageFiltered(url, this.filterCriteria, pageSize, postContent)
      .subscribe((listPagingResponse: LMListPagingResponse) => {

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.status === AppConstants.STATUS_COMPLETED) {

          // todo kris can any of this be moved to a service? lm-list-tree.service.ts?

          // If parentDataView add new data to child
          // Else add new data to root
          if (parentDataView) {
            let parentResourceId = parentDataView.resourceId;
            if (isCubeView) {
              // Cube viewer uses dataviewSet to chart data.
              parentDataView.dataViewSet.data = parentDataView.dataViewSet.data.concat(listPagingResponse.dataViewSet.data);
              parentResourceId = parentDataView.dataViewResId;
            }

            // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
            this.treeData.map(function callback(dataView: LMListDataView) {
              const resourceId = isCubeView ? dataView.dataViewResId : dataView.resourceId;
              if (parentResourceId.includes(resourceId)) {

                // Copy moreLink from array
                const moreLink = dataView.children[dataView.children.length - 1];
                moreLink.pagingUrls = listPagingResponse.dataViewSet.pagingUrls;

                // Remove more link from array
                dataView.children.splice(-1, 1);

                // Format new data for tree list
                const newData = listPagingResponse.dataViewSet.data.map((d: LMListDataView, index) => {
                  d.depth = dataView.children[0].depth;
                  d.id = dataView.children[dataView.children.length - 1].id + index;
                  d.parentId = parentDataView.id;
                  // Add empty child array. If this isn't added then the datagrid control will not display the
                  // expand collapse icon next to the data.
                  if (!d.isLeaf) {
                    d.children = [];
                  }

                  return d;
                });

                // Update array inserting new data into array, re-add moreLink
                if (listPagingResponse.dataViewSet.pagingInfo.hasNext) {
                  dataView.children = dataView.children.concat(newData, moreLink);
                } else {
                  dataView.children = dataView.children.concat(newData);
                }
              }
              if (Array.isArray(dataView.children) && dataView.children.length !== 0) {
                dataView.children.map(callback);
              }
            });
          } else {
            // Copy moreLink from array
            // Update paging urls
            const moreLink = this.treeData[this.treeData.length - 1];
            moreLink.pagingUrls = listPagingResponse.dataViewSet.pagingUrls;

            // Remove more link from array
            this.treeData.splice(-1, 1);

            // Get id of last item
            const id = this.treeData[this.treeData.length - 1].id;

            // Format new data for tree list
            const newData = listPagingResponse.dataViewSet.data.map((d: LMListDataView, index) => {
              d.depth = 1;
              d.id = id + (index + 1);
              d.parentId = 0;
              d.children = [];
              return d;
            });

            // Update array inserting new data into array, re-add moreLink
            if (listPagingResponse.dataViewSet.pagingInfo.hasNext) {
              this.treeData = this.treeData.concat(newData, moreLink);
            } else {
              this.treeData = this.treeData.concat(newData);
            }

            if (isCubeView) {
              // Cube Viewer uses DataViewSet in charting.
              this.dataViewSet.data = this.dataViewSet.data.concat(listPagingResponse.dataViewSet.data);
            }
          }

          this.update(listPagingResponse, false);
          this.changeDetectorRef.markForCheck();
          // Call is performed in a timeout to make sure the changes to the
          // tree have occurred before attempting to set an active row
          setTimeout(() => {
            this.performSelectionForMode(this.listState.activatedRow);
            this.lmListEventService.listGridAfterPagingEvent(listPagingResponse);
          });
        }
      });
  }

  private disableComponent() {
    this.treeData = [];

    if (this.sohoDataGridComponent) {
      this.sohoDataGridComponent.removeSelected();
    }

    this.setEmptyMessage();

    this.changeDetectorRef.markForCheck();
  }

  private postLoad(listPagingResponse: LMListPagingResponse) {

    this.sohoDataGridComponent.unSelectAllRows();

    if (this.listState.filterCriteria && this.listState.filterCriteria.length > 0) {
      this.setNoDataMessage(true);
    } else {
      this.setEmptyMessage();
    }

    this.dataViewSet = listPagingResponse.dataViewSet;
    this.savedSearches = listPagingResponse.savedSearchDataViewSet;
    this.maxLevels = listPagingResponse.dataViewSet.maxLevels;
    this.showLevels = listPagingResponse.dataViewSet.showLevels;

    if (this.formatColumnHeaderMessageRules) {
      this.updateColumnLabels(this.listSpec, this.dataViewSet);
    }

    this.updateFilterBar();

    this.updateColumnVisibility(this.listSpec, this.dataViewSet);
    this.formatDataViewsForTree(listPagingResponse);
    this.update(listPagingResponse, true);
  }

  private columnHeadersHaveMessageRules(): boolean {
    const headersWithMessageRules = this.gridOptions.columns.filter((column: any) => column.displayField && column.displayField.label && column.displayField.label.variables);

    let groupHeadersWithMessageRules = [];
    if (this.gridOptions.columnGroups) {
      groupHeadersWithMessageRules = this.gridOptions.columnGroups.filter((columnGroup: SohoDataGridColumnGroup) => columnGroup.name && columnGroup.name.includes('{0}'));
    }

    return headersWithMessageRules.length > 0 || groupHeadersWithMessageRules.length > 0;
  }

  private dataLoaded(pagingResponse: LMListPagingResponse) {
    if (!this.sohoDataGridComponent) {
      return;
    }

    /**
     * Wait for data to be rendered into the datagrid by calling this code
     * in the next angular tick().
     */
    setTimeout(() => {
      if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
        this.cubeDataLoadedEvent(pagingResponse);
      } else {
        this.dataLoadedEvent(pagingResponse);
      }
    });
  }

  private dataLoadedEvent(pagingResponse: LMListPagingResponse) {
    this.lmListEventService.dataLoadedEvent(pagingResponse);
  }

  private cubeDataLoadedEvent(pagingResponse: LMListPagingResponse) {
    this.lmListEventService.cubeDataLoadedEvent(pagingResponse);
  }

  private getChildren(dataViewSet: LMDataViewSet, parentDataView: LMListDataView): Array<LMListDataView> {
    const children = [];
    const data = dataViewSet.data;
    let depth = parentDataView.depth + 1;

    for (let i = 0, l = data.length; i < l; i++) {
      // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
      // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
      // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
      // const childrenUrl = compiledUrlTemplate.expand({ resourceId: dataView.resourceId });

      // todo khollenbeck ppatton how to deal with a childrenUrl being undefined with new listSpec.childrenUrl template?
      const currentData: LMDataView = data[i];
      const treeDataView: LMListDataView = Object.assign({
        depth,
        parentId: parentDataView.id,
        children: this.hasChildren(currentData)
      }, currentData);

      if (treeDataView.children === undefined) {
        delete treeDataView.children;
      }

      for (const key in treeDataView.fields) {
        if (!treeDataView.fields.hasOwnProperty(key)) {
          continue;
        }

        treeDataView[key] = treeDataView.fields[key].value;
      }

      // SET ID
      treeDataView.id = this.getID();

      if (treeDataView.dataViewSet && depth <= this.showLevels) {
        depth++;
        // treeDataView.expanded = true;
        treeDataView.children = this.getChildren(treeDataView.dataViewSet, treeDataView);
      }

      children.push(treeDataView);

      if (i + 1 === l && dataViewSet.pagingInfo.hasNext) {
        children.push(<LMListTreeMoreLink>{
          depth,
          isTreeMoreLink: true,
          pagingUrls: dataViewSet.pagingUrls,
          id: treeDataView.id + 1,
          cubeInfo: this.lmListSpecUtilService.isCubeView(this.listSpec) ? { cubeMediator: 'cubeList' } : undefined
        });
      }
    }

    return children;
  }

  private setSelectedTreeDataView(dataView: LMListDataView) {
    if (dataView && !dataView.isTreeMoreLink) {
      this._selectedTreeDataView = dataView;
    } else {
      this._selectedTreeDataView = null;
    }
  }

  private update(listPagingResponse: LMListPagingResponse, fireDataLoadedMessage: boolean, activeRow?: number) {
    if (activeRow !== undefined) {
      this.listState.activatedRow = activeRow;
    }

    this.treeData = [].concat(this.treeData);

    this.listState.dataViewSet = this.dataViewSet;

    this.listState.treeData = this.treeData.length > 0 ? Flatted.stringify(this.treeData) : undefined;

    if (listPagingResponse.searchDataView) {
      this.searchFormDataView = listPagingResponse.searchDataView;
      this.listState.searchFormDataView = this.searchFormDataView;
    }

    if (fireDataLoadedMessage) {
      this.dataLoaded(listPagingResponse);
    }

    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      this.unSelectAllRows();
    } else {
      // change to how row selection works. should activate row when
      // collapsing/uncollapsing - Phillip 2/5/2020
      this.performSelectionForMode(activeRow);
    }
  }

  /**
   * Flattens dataViewSet for Hierarchy and Tree lists
   * @param data
   */
  private buildFlatDataViewArray(): void {
    // Reset
    this.flatDataViewArray = [];

    // Create New
    const flatDataViewArray = [];

    // Push root or multi roots
    this.treeData.forEach((d) => {
      flatDataViewArray.push(d);
      if (d.children) {
        flatten(d.children);
      }
    });

    /**
     * Recurse children
     * @param children
     */
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    function flatten(data?: Array<LMListDataView|LMListTreeMoreLink>) {
      data.forEach((d) => {
        flatDataViewArray.push(d);

        if (d.children && d.children.length > 0) {
          flatten(d.children);
        }
      });
    }

    // todo change to using arrow function
    // const flatten = (data?: Array<LMListDataView|LMListTreeMoreLink>) => {
    //   data.forEach((d) => {
    //     flatDataViewArray.push(d);
    //
    //     if (d.children && d.children.length > 0) {
    //       flatten(d.children);
    //     }
    //   });
    // };

    // Set new array
    this.flatDataViewArray = flatDataViewArray;
  }

  private loadFromState() {
    if (this.dataViewSet === undefined && this.listState.dataViewSet !== undefined) {
      this.dataViewSet = this.listState.dataViewSet;
    }

    if (this.listState.treeData) {
      this.treeData = Flatted.parse(this.listState.treeData);
      this.remove_selectedFlag(this.treeData);
      this.refresh().subscribe();
    }
  }

  private addCubeOverrideInfo(url: string): string {
    if (this.listState.cubeOverrides) {
      const delimiter = (url.indexOf('?') === -1) ? '?' : '&';
      const params = this.lmUrlUtilService.cubeOverridesToQueryString(this.listState.cubeOverrides, delimiter);
      url = url + params;
    }
    return url;
  }

  public cubeSelectRows(rowsToSelect: Array<number>, scrollIntoView?: boolean) {
    this.setSelectedRows(rowsToSelect, scrollIntoView);
  }
  public findAllLevel1RowIndices(): Array<number> {

    const treeRows: Array<LMListDataView> = this.getLMListDataViews(this.treeData);

    // No chart for cubes that has more data link.
    if (treeRows.length !== this.treeData.length) {
      return [];
    }

    const rowIndices: Array<number> = [];
    treeRows.filter((m, index) => {
      if (m.depth === 1) {
        rowIndices.push(index);
      }
    });

    return rowIndices;
  }

  private getLMListDataViews(treeData: Array<LMListDataView|LMListTreeMoreLink>): Array<LMListDataView> {
    let treeRows: Array<LMListDataView> = [];
    // typescript does not like filter. Cant figure out the type from union types.
    // treeRows = this.treeData.filter(m => this.isLMListDataView(m));
    treeData.forEach(m => {
      if (this.isLMListDataView(m)) {
        treeRows = treeRows.concat(<LMListDataView>m);
      }
    });
    return treeRows;
  }

  private isLMListDataView(treeRow: LMListDataView | LMListTreeMoreLink): treeRow is LMListDataView {
    return treeRow.isTreeMoreLink === undefined;
  }

  private findParentNode(rowDataView: LMListDataView, treeRows: Array<LMListDataView>): LMListDataView {
    if (rowDataView.parentId === 0) {
      return null; // Root
    }
    // TODO: May be I can use findIndex with a callback function that is recursive.
    let parentDataView = null;
    treeRows.some( (treeRow) => {
      if (treeRow.id === rowDataView.parentId) {
        parentDataView = treeRow;
        return true;
      }
      if (Array.isArray(treeRow.children) && treeRow.children.length > 0) {
        parentDataView = this.findParentNode(rowDataView, this.getLMListDataViews(treeRow.children));
        if (parentDataView !== null) {
          return true;
        }
      }
    });

    return parentDataView;
  }

  private updateDimensionColumnHeader(rowDataView: LMListDataView) {
    if (this.lmListSpecUtilService.isCubeView(this.listSpec)) {
      if (rowDataView?.isTreeMoreLink) {
        return;
      }
      const gridColumns: ListGridColumn[] = this.sohoDataGridComponent.getColumns();
      if (gridColumns) {
        const dimHeader = rowDataView.fields['Dimension_cube_view_dimension'].dimFieldLabel;
        gridColumns[0].name = dimHeader;
        this.sohoDataGridComponent.updateColumns(gridColumns, this.sohoDataGridComponent.getColumnGroups());
      }
    }
  }

  private updateColumnsAfterPeriodOverride(dataViewSet: LMDataViewSet, reset = false) {
    // new column headers will have the new column or group headers after a period override.
    // Suppose the period is changed from 12/1/2018 to 12/1/2015, the column header will be changed from Dec 2018 to Dec 2015.
    // And addtlFilter string will also reflect a change in filter field values.
    // 4/7/2021: Changed it to return the entire set of display fields.
    // Changing period from 6/1/2021 to 7/1/2021 when monthly period view is selected, will return 31 columns instead of 30.
    // Or changing it to 2/1/2021 will return 28 columns. This requires the entire set of new display fields are returned after override.
    // Generate the new grid columns, grid column groups and translate the labels before updateColumns.
    if (this.lmListSpecUtilService.isCubeView(this.listSpec) && dataViewSet.newColumnHeader && dataViewSet.newColumnHeader.length > 0) {
      // Build the new columns and column groups based on listSpecClone
      const listSpecClone: LMListSpec = JSON.parse(JSON.stringify(this.listSpec));
      listSpecClone.busListSpec.displayFields = dataViewSet.newColumnHeader;
      this.buildAndUpdateColumns(listSpecClone, dataViewSet);
    } else if (reset) {
      // Build the new columns and column groups based on listSpec
      this.buildAndUpdateColumns(this.listSpec, dataViewSet);
    }
  }

  private buildAndUpdateColumns(busListSpecParam: LMListSpec, dataViewSet: LMDataViewSet) {
    const gridColumns = this.lmListGridBuilderService.buildColumnsAfterPeriodOverride(this.listUrlInfo, busListSpecParam, this.listState, this.listPersonalization);
    const gridColumnGroups = this.lmListGridBuilderService.buildColumnGroups(busListSpecParam, false);
    gridColumns
      .map(column => <ListGridColumn> column)
      .forEach((column: ListGridColumn) => {
        if (column.isActionable) {
          column.click = (event, args) => this.onColumnClicked(column, event, args);
        }
      });
    this.lmListUtilService.updateColumnLabels(busListSpecParam, dataViewSet, gridColumns, gridColumnGroups);
    this.sohoDataGridComponent.updateColumns(gridColumns, gridColumnGroups);
  }

  private allowsLoadingFromPageState() {
    if (this.lmContextService.disablePageState()) {
      return false;
    }

    if (this.listState.cubeOverrides?.periodView
      || this.listState.cubeOverrides?.pivotView
      || this.listState.cubeOverrides?.columnView
      || this.listState.cubeOverrides?.periodOverrides?.overridePeriods) {
      if (this.listState.cubeOverrides?.cubeDrillNavigation) {
        this.listState.cubeOverrides.cubeDrillNavigation = false;
        return true;
      }
      return false;
    }

    return true;
  }

  /**
   * treedata may have an _selected property set. This property is being set
   * internally in datagrid.js. When we store that datagrid data in local storage
   * we end up storing that _select flag along with the data. This causes a
   * strange "ghost" selection when loading that data back into the tree grid.
   *
   * The fix is to recurse the tree data and remove any _selected flags.
   */
  private remove_selectedFlag(treeData: Array<LMListDataView|LMListTreeMoreLink>) {
    const removeFlag = (dataView: LMListDataView) => {
      // @ts-ignore get around this private variable not in the typescript definition
      delete dataView._selected;

      if (Array.isArray(dataView.children) && dataView.children?.length !== 0) {
        dataView.children.forEach(removeFlag);
      }
    };

    treeData.forEach(removeFlag);
  }

  /**
   * +- Icon is displayed when this is true
   * @param dataView
   * @private
   */
  private hasChildren(dataView: LMDataView) {
    const isTotalingRow = dataView.isSubtotalView || dataView.isSubtotalSummaryView;
    const hasChildren = (dataView.dataViewSet !== undefined || dataView.childrenUrl !== undefined) && !isTotalingRow;

    return hasChildren ? [] : isTotalingRow ? undefined : null;
  }
}
